import React, { useState, useEffect } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../firebase";
import TeamPlayer from "./TeamPlayer";

const Teams = ({ gameId }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [playerFilter, setPlayerFilter] = useState(null);
  const [view, setView] = useState("teams"); // 'teams' or 'players'

  useEffect(() => {
    if (!gameId) return;

    const teamsRef = ref(db, `games/${gameId}/teams`);

    const unsubscribe = onValue(teamsRef, (snapshot) => {
      if (snapshot.exists()) {
        const teamsData = snapshot.val();
        const teamsArray = [];

        Object.entries(teamsData).forEach(([id, team]) => {
          const playerCount = team.players ? Object.keys(team.players).length : 0;
          teamsArray.push({
            id,
            ...team,
            playerCount,
          });
        });

        setTeams(teamsArray);
      } else {
        setTeams([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [gameId]);

  const handleTeamClick = (team) => {
    setSelectedTeam(team);
    setPlayerFilter(null);
    setView("players");
  };

  const handleFilterClick = (status) => {
    setSelectedTeam(null);
    setPlayerFilter(status);
    setView("players");
  };

  const handleBackToTeams = () => {
    setView("teams");
    setSelectedTeam(null);
    setPlayerFilter(null);
  };

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show TeamPlayer component
  if (view === "players") {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            className="btn btn-secondary"
            onClick={handleBackToTeams}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back to Teams
          </button>
          <h4 className="mb-0">
            {selectedTeam ? (
              <>
                <i className="bi bi-shield-fill me-2"></i>
                {selectedTeam.teamName}
              </>
            ) : (
              <>
                <i className="bi bi-funnel-fill me-2"></i>
                {playerFilter === "upcoming" && "Upcoming Players"}
                {playerFilter === "unsold" && "Unsold Players"}
                {playerFilter === "sold" && "Sold Players"}
              </>
            )}
          </h4>
          <div></div>
        </div>

        <TeamPlayer
          gameId={gameId}
          teamId={selectedTeam?.id || null}
          status={playerFilter}
        />
      </div>
    );
  }

  // Show Teams Grid
  return (
    <div className="container mt-4">
      {/* Filter Buttons */}
      <div className="mb-4">
        <h4 className="mb-3">
          <i className="bi bi-funnel me-2"></i>
          View Players by Status
        </h4>
        <div className="d-flex flex-wrap gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={() => handleFilterClick("upcoming")}
          >
            <i className="bi bi-clock-history me-2"></i>
            Upcoming Players
          </button>
          <button
            className="btn btn-outline-danger"
            onClick={() => handleFilterClick("unsold")}
          >
            <i className="bi bi-person-x me-2"></i>
            Unsold Players
          </button>
          <button
            className="btn btn-outline-success"
            onClick={() => handleFilterClick("sold")}
          >
            <i className="bi bi-check-circle me-2"></i>
            Sold Players
          </button>
        </div>
      </div>

      {/* Teams Grid */}
      <div>
        <h4 className="mb-4">
          <i className="bi bi-shield-shaded me-2"></i>
          All Teams ({teams.length})
        </h4>

        {teams.length === 0 ? (
          <div className="alert alert-info">
            <i className="bi bi-info-circle me-2"></i>
            No teams found. Create teams to get started.
          </div>
        ) : (
          <div className="row g-4">
            {teams.map((team) => (
              <div key={team.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                <div
                  className="card h-100 shadow-sm hover-card"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleTeamClick(team)}
                >
                  {/* Team Banner/Image */}
                  <div 
                    className="position-relative overflow-hidden"
                    style={{ height: "200px" }}
                  >
                    
                      <img
                        src={team.teamBanner || "/team.jpg"}
                        alt={team.teamName}
                        className="w-100 h-100"
                        style={{ objectFit: "cover" }}
                      />
                    
                    
                    {/* Team Name Overlay */}
                    <div 
                      className="position-absolute bottom-0 start-0 w-100 p-3"
                      style={{
                        background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)"
                      }}
                    >
                      <h5 className="text-white fw-bold mb-0">{team.teamName}</h5>
                    </div>

                    {/* Player Count Badge */}
                    <span className="position-absolute top-0 end-0 m-2 badge bg-dark">
                      <i className="bi bi-people-fill me-1"></i>
                      {team.playerCount}
                    </span>
                  </div>

                  {/* Team Details */}
                  <div className="card-body">
                    <div className="mb-2">
                      <i className="bi bi-wallet2 text-success me-2"></i>
                      <strong>Purse Left:</strong>
                      <div className="fs-5 text-success fw-bold">
                        ৳{team.purseLeft?.toLocaleString() || 0}
                      </div>
                    </div>

                    <div className="mb-2">
                      <i className="bi bi-people text-primary me-2"></i>
                      <strong>Players:</strong>
                      <span className="badge bg-primary ms-2">
                        {team.playerCount}
                      </span>
                    </div>

                    {team.captainPlayerName && (
                      <div className="mb-2">
                        <i className="bi bi-star-fill text-warning me-2"></i>
                        <strong>Captain:</strong>
                        <div className="text-muted small">{team.captainPlayerName}</div>
                      </div>
                    )}

                    {team.createdAt && (
                      <div className="text-muted small">
                        <i className="bi bi-calendar-event me-1"></i>
                        Created: {new Date(team.createdAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  {/* Click to View Button */}
                  <div className="card-footer bg-light border-0">
                    <button className="btn btn-primary btn-sm w-100">
                      <i className="bi bi-eye me-2"></i>
                      View Team Players
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .hover-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
        }
      `}</style>
    </div>
  );
};

export default Teams;